import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import configData from '../../config.json';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';
import { useStampWallet } from '../../context/stampWallet';
import { isVoucherReady } from '../../utils/stampcard';

// In-cart reward redemption (§9.3). Renders after <LoyaltyBanner>. The reward
// CHOICE happens here (a radio of the voucher's rewardOptions); choosing stages
// the voucher in the wallet context so checkout can attach
// redeemVoucher:{voucherCode, rewardProductId} to the order payload.
//
// Fail-safe-to-nothing: anonymous / no wallet / no Version:2 vouchers → the
// context exposes vouchers=[] and we render null. The server is authoritative on
// price/expiry/eligibility and constructs the reward line — the client never
// sends isReward/price, only the staged choice (A6 / §7.4 / §11 C4).

// One card per voucher: lazily loads its reward options and renders the picker.
const VoucherCard = ({
  voucher,
  currency,
  cartById = {},
  walletOwnerToken,
  staged,
  onChoose,
  onClear,
  onStagedValue,
}) => {
  const { language } = useDataContext();
  const [options, setOptions] = useState(null); // null = loading, [] = none
  const [error, setError] = useState(false);
  const [rewardRule, setRewardRule] = useState(null);
  const [priceCap, setPriceCap] = useState(null);

  // The reward makes an item the customer ALREADY has in the cart free: keep only
  // options whose product is in the cart, and compute the discount from the CART
  // unit price (capped for UPTO_CAP) so the client total matches the server.
  const cap = priceCap != null ? Number(priceCap) : null;
  const cartOptions = (Array.isArray(options) ? options : [])
    .map((o) => {
      const cartPrice = cartById[String(o.productId || '')];
      if (cartPrice == null) return null;
      // RewardMaxValue caps the giveaway under EVERY rule (server:
      // validateVoucherRedemption) — not just UPTO_CAP. A FREE_ANY reward with a
      // cap set makes the item free only up to the cap; the customer pays the rest.
      // Mirror that here so the displayed discount/total matches what the server
      // charges (previously the cap was honoured only for UPTO_CAP → a capped
      // FREE_ANY reward showed "FREE" but the server charged the residual).
      const discount = cap != null ? Math.min(cartPrice, cap) : cartPrice;
      return { ...o, cartPrice, discount, payable: Math.max(0, cartPrice - discount) };
    })
    .filter(Boolean);
  // CHEAPEST_ELIGIBLE is auto: the server discounts the cheapest eligible cart
  // line regardless of choice, so the reported discount is that minimum — using
  // the already-capped per-option discount so the cap binds here too.
  const cheapestCartDiscount = cartOptions.length
    ? Math.min(...cartOptions.map((o) => o.discount))
    : 0;

  const voucherId = voucher?._id || voucher?.id;
  const voucherCode = voucher?.Code || voucher?.code;

  // Is THIS voucher the one currently staged? The context's stagedVoucher shape
  // is { voucher, rewardProductId } (§9.1) — match on either id or code.
  const isStaged =
    !!staged &&
    (String(staged.voucher?._id || staged.voucher?.id || '') === String(voucherId || '') ||
      String(staged.voucher?.Code || staged.voucher?.code || '') === String(voucherCode || ''));
  const stagedProductId = isStaged ? staged.rewardProductId : null;

  // Report the staged choice's display discount (free-item value minus surcharge)
  // up to YourItems. Computed HERE because the card holds the loaded option prices
  // (the context's stagedVoucher carries no price). Always report (incl. 0) so a
  // cleared/changed choice never leaves a stale value behind. Server is
  // authoritative at checkout — this is display-only.
  const onStagedValueRef = useRef(onStagedValue);
  onStagedValueRef.current = onStagedValue;
  useEffect(() => {
    if (!onStagedValueRef.current) return;
    if (!stagedProductId) return;
    const opt = cartOptions.find((o) => o.productId === stagedProductId);
    if (!opt) {
      // Staged product isn't a cart item (server would reject) → no discount.
      onStagedValueRef.current(0);
      return;
    }
    // The discount is the freed cart unit's value (capped for UPTO_CAP), which is
    // exactly what the server subtracts. CHEAPEST_ELIGIBLE discounts the cheapest
    // eligible cart line regardless of choice, so report that minimum.
    const value =
      rewardRule === 'CHEAPEST_ELIGIBLE' ? cheapestCartDiscount : opt.discount;
    onStagedValueRef.current(Number(value) || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedProductId, options, rewardRule, priceCap]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!voucherId) {
        if (!cancelled) {
          setOptions([]);
          setError(false);
        }
        return;
      }
      try {
        // A4: identity travels in the Authorization header, never a query param.
        const response = await axios({
          method: 'POST',
          url: `${configData.SERVER_URL}/stamp/voucher/${voucherId}/options`,
          headers: { Authorization: `Bearer ${walletOwnerToken || ''}` },
        });
        if (cancelled) return;
        setOptions(Array.isArray(response?.data?.options) ? response.data.options : []);
        setRewardRule(response?.data?.rewardRule || null);
        setPriceCap(response?.data?.priceCap != null ? response.data.priceCap : null);
        setError(false);
      } catch (e) {
        // Fail-safe: an unloadable voucher simply offers no reward — never block
        // the cart. The server stays authoritative at checkout.
        if (cancelled) return;
        setOptions([]);
        setError(true);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [voucherId, walletOwnerToken]);

  // Loading / error / no reward at all → render nothing for this voucher.
  if (options === null || error || options.length === 0) return null;

  // The reward exists but none of its eligible products is in the cart yet → a
  // subtle hint so the customer knows to add one (rather than hiding it).
  if (cartOptions.length === 0) {
    const names = options.map((o) => o.name).filter(Boolean).slice(0, 3).join(', ');
    return (
      <div className="border border-amber-200 rounded-xl px-[18px] py-[14px] mb-4 bg-gradient-to-br from-amber-50 to-amber-100">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <span className="text-xl">🎁</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900">
              {translations[language]?.voucher_reward_available || 'Free reward available'}
            </div>
            <div style={{ fontSize: 12, color: '#b45309' }}>
              {(
                translations[language]?.voucher_add_to_use ||
                'Add {item} to your order to redeem it free'
              ).replace('{item}', names)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-amber-200 rounded-xl px-[18px] py-[14px] mb-4 bg-gradient-to-br from-amber-50 to-amber-100">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
          <span className="text-xl">🎁</span>
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-amber-900">
            {voucher?.RewardSnapshot?.RewardName ||
              voucher?.StampName ||
              translations[language]?.voucher_redeem_title ||
              'Redeem reward'}
          </div>
          <div style={{ fontSize: 12, color: '#b45309' }}>
            {translations[language]?.voucher_choose_reward || 'Choose your reward'}
          </div>
        </div>
        {stagedProductId && (
          <button
            type="button"
            onClick={() => onClear()}
            className="text-xs font-bold text-amber-700 hover:underline"
          >
            {translations[language]?.voucher_remove || 'Remove'}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {cartOptions.map((opt) => {
          const selected = stagedProductId === opt.productId;
          const payable = Number(opt.payable || 0);
          const isFree = payable <= 0;
          const disabled = !opt.available;

          return (
            <button
              key={opt.productId}
              type="button"
              disabled={disabled}
              onClick={() => onChoose(opt.productId)}
              className={`w-full flex items-center justify-between text-left rounded-lg px-3 py-2 border transition-colors ${
                disabled
                  ? 'opacity-50 cursor-not-allowed border-[#f0f0f0] bg-white'
                  : selected
                  ? 'border-amber-500 bg-white'
                  : 'border-[#f0f0f0] bg-white hover:border-amber-300'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span
                  className={`w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${
                    selected ? 'border-amber-500' : 'border-gray-300'
                  }`}
                >
                  {selected && <span className="w-2 h-2 rounded-full bg-amber-500" />}
                </span>
                <span className="text-sm font-bold truncate">{opt.name}</span>
              </span>
              <span className="text-xs font-bold shrink-0 ml-2">
                {disabled ? (
                  <span className="text-gray-400">
                    {translations[language]?.voucher_sold_out || 'Sold out today'}
                  </span>
                ) : isFree ? (
                  <span className="flex items-center gap-1.5">
                    <span style={{ textDecoration: 'line-through', color: '#999' }}>
                      {currency} {Number(opt.cartPrice).toFixed(2)}
                    </span>
                    <span className="text-green-600">
                      {translations[language]?.voucher_free || 'FREE'}
                    </span>
                  </span>
                ) : (
                  <span className="text-amber-700">
                    {translations[language]?.voucher_you_pay || 'you pay'} {currency}{' '}
                    {payable.toFixed(2)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const VoucherRedeemBlock = ({ restaurant, currency = 'DKK', items = [], onVoucherDiscount }) => {
  // The reward makes an item the customer ALREADY has in the cart free, so we
  // map each cart product id → its unit price. Only options present here are
  // offered, and the discount is the cart price (server uses the same).
  const cartById = {};
  (Array.isArray(items) ? items : []).forEach((it) => {
    const pid = String(it?._id || it?.id || '');
    if (!pid) return;
    const unit = Number(it?.price);
    if (Number.isFinite(unit) && cartById[pid] == null) cartById[pid] = unit;
  });

  // useStampWallet() returns undefined when no StampWalletWrapper is mounted;
  // coalesce so this block is a no-op (fail-safe-to-nothing) rather than throwing.
  const wallet = useStampWallet() || {};
  const {
    vouchers = [],
    stagedVoucher = null,
    stageVoucher,
    clearStagedVoucher,
    setVoucherDiscount,
    walletOwnerToken,
  } = wallet;

  // Mirror LoyaltyBanner: keep the latest parent callback callable without making
  // it a dependency (parents pass a fresh inline callback each render).
  const onVoucherDiscountRef = useRef(onVoucherDiscount);
  onVoucherDiscountRef.current = onVoucherDiscount;

  // Only vouchers redeemable in THIS restaurant are offered in-cart. Mirrors the
  // server's allowedAtLocation(): a non-empty RedeemableAt is the allow-list (a
  // chain voucher earned at one venue is redeemable at its affiliates), and an
  // empty one means unrestricted, in which case we fall back to the issuer.
  const restaurantId = restaurant?._id;
  const eligibleVouchers = (Array.isArray(vouchers) ? vouchers : []).filter((v) => {
    if (!restaurantId) return false;
    // Never offer a spent or lapsed voucher: the server rejects it silently and
    // the order goes through at full price after we quoted a discount.
    if (!isVoucherReady(v)) return false;
    const allowList = Array.isArray(v?.RedeemableAt) ? v.RedeemableAt : [];
    if (allowList.length) {
      return allowList.some((l) => String(l?._id || l) === String(restaurantId));
    }
    const vrId = v?.Restaurant?._id || v?.Restaurant || v?.RestaurantId;
    return !vrId || String(vrId) === String(restaurantId);
  });

  // When nothing is staged, clear any previously-reported discount so a removed
  // voucher never leaves a stale value behind. The non-zero value is reported by
  // the staged VoucherCard (which holds the loaded option prices).
  useEffect(() => {
    if (!stagedVoucher || !stagedVoucher.rewardProductId) {
      if (onVoucherDiscountRef.current) onVoucherDiscountRef.current(0);
      if (typeof setVoucherDiscount === 'function') setVoucherDiscount(0);
    }
  }, [stagedVoucher]);

  // Fail-safe-to-nothing: nothing redeemable here → render nothing.
  if (!restaurantId || eligibleVouchers.length === 0 || typeof stageVoucher !== 'function') {
    return null;
  }

  const handleChoose = (voucher, productId) => {
    // v1: one voucher per order — staging a new choice replaces any previous.
    // The context signature is stageVoucher(voucher, rewardProductId); the option's
    // price snapshot is display-only and recomputed locally, so it is not persisted.
    stageVoucher(voucher, productId);
  };

  return (
    <>
      {eligibleVouchers.map((voucher) => (
        <VoucherCard
          key={voucher?._id || voucher?.id || voucher?.Code}
          voucher={voucher}
          currency={currency}
          cartById={cartById}
          walletOwnerToken={walletOwnerToken}
          staged={stagedVoucher}
          onChoose={(productId) => handleChoose(voucher, productId)}
          onClear={() => clearStagedVoucher && clearStagedVoucher()}
          onStagedValue={(amt) => {
            if (onVoucherDiscountRef.current) onVoucherDiscountRef.current(amt);
            if (typeof setVoucherDiscount === 'function') setVoucherDiscount(amt);
          }}
        />
      ))}
    </>
  );
};

export default VoucherRedeemBlock;
